import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@app/components/shared/Button";

/**
 * Optional price reference points to display above the chart so the admin
 * can visually anchor the current position's entry, stop, and target levels.
 * The TradingView free widget does not expose a way to draw price lines via
 * URL parameters, so these are rendered as a compact info strip instead.
 */
export interface ChartReference {
  entryPrice?: number | null;
  stopLoss?: number | null;
  takeProfit?: number | null;
  side?: "long" | "short";
}

interface TradingViewChartModalProps {
  symbol: string;
  onClose: () => void;
  reference?: ChartReference | null;
}

function toTradingViewSymbol(symbol: string): string {
  // Converts "ICNT/USDT" to "BYBIT:ICNTUSDT" (Bybit spot market on TradingView).
  const compact = symbol.replace("/", "");
  return `BYBIT:${compact}`;
}

function buildTradingViewChartUrl(symbol: string): string {
  // TradingView Advanced Chart widget embed (free). Keeps the toolbar visible so
  // the admin can switch timeframes, add indicators, and use basic drawing tools.
  const tvSymbol = encodeURIComponent(toTradingViewSymbol(symbol));
  const params = [
    `symbol=${tvSymbol}`,
    "interval=5",
    "hidesidetoolbar=0",
    "symboledit=1",
    "saveimage=1",
    "theme=dark",
    "style=1",
    "timezone=exchange",
    "withdateranges=1",
    "hide_side_toolbar=0",
    "allow_symbol_change=1",
    "details=1",
    "hotlist=0",
    "calendar=0",
    "show_popup_button=1",
    "popup_width=1000",
    "popup_height=650",
  ];
  return `https://s.tradingview.com/widgetembed/?${params.join("&")}`;
}

function formatLevel(value: number | null | undefined): string {
  const n = Number(value);
  if (value == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

/**
 * Fullscreen-style TradingView chart modal rendered through a React Portal so
 * it escapes any ancestor with `backdrop-filter` (which creates a containing
 * block for `position: fixed` and would otherwise trap the overlay).
 */
export function TradingViewChartModal({
  symbol,
  onClose,
  reference = null,
}: TradingViewChartModalProps): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    setExpanded(false);
    setMinimized(false);
  }, [symbol]);

  if (!symbol) return null;

  const chartUrl = buildTradingViewChartUrl(symbol);

  const modalClass = [
    "tv-chart-modal",
    expanded ? "tv-chart-modal--expanded" : "",
    minimized ? "tv-chart-modal--minimized" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const hasReference =
    reference &&
    (reference.entryPrice != null ||
      reference.stopLoss != null ||
      reference.takeProfit != null ||
      reference.side);

  const overlay = (
    <div className="tv-chart-overlay" onClick={onClose}>
      <div className={modalClass} onClick={(e) => e.stopPropagation()}>
        <div className="tv-chart-header">
          <div className="tv-chart-title">
            <h3>{symbol} Chart</h3>
            {!minimized && (
              <p className="tv-chart-subtitle">
                Powered by TradingView — switch timeframes and add indicators from the chart toolbar.
              </p>
            )}
          </div>
          <div className="tv-chart-controls">
            <Button
              type="button"
              variant="ghost"
              size="small"
              title={minimized ? "Restore" : "Minimize"}
              onClick={() => setMinimized((v) => !v)}
            >
              {minimized ? "Restore" : "Minimize"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="small"
              title={expanded ? "Restore size" : "Expand to full tab"}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Restore" : "Expand"}
            </Button>
            <Button type="button" variant="ghost" size="small" title="Close" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
        {!minimized && hasReference && (
          <div className="tv-chart-reference">
            {reference?.side && (
              <span className="tv-chart-reference__item tv-chart-reference__item--side">
                <span className="tv-chart-reference__label">Side</span>
                <strong>{reference.side === "short" ? "Short" : "Long"}</strong>
              </span>
            )}
            {reference?.entryPrice != null && (
              <span className="tv-chart-reference__item tv-chart-reference__item--entry">
                <span className="tv-chart-reference__label">Entry</span>
                <strong>{formatLevel(reference.entryPrice)}</strong>
              </span>
            )}
            {reference?.stopLoss != null && (
              <span className="tv-chart-reference__item tv-chart-reference__item--stop">
                <span className="tv-chart-reference__label">Stop</span>
                <strong>{formatLevel(reference.stopLoss)}</strong>
              </span>
            )}
            {reference?.takeProfit != null && (
              <span className="tv-chart-reference__item tv-chart-reference__item--target">
                <span className="tv-chart-reference__label">Target</span>
                <strong>{formatLevel(reference.takeProfit)}</strong>
              </span>
            )}
          </div>
        )}
        {!minimized && (
          <div className="tv-chart-frame">
            <iframe
              src={chartUrl}
              title={`${symbol} chart`}
              className="tv-chart-iframe"
              frameBorder="0"
              allowFullScreen
            />
          </div>
        )}
      </div>
    </div>
  );
  // Portal to document.body so the fixed overlay escapes any ancestor with
  // backdrop-filter, which would otherwise trap position:fixed inside the card.
  return createPortal(overlay, document.body);
}
